import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { AuthContext } from "../../../context/authcontext";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";
import FieldConfigurationSidebar from "../../../components/Tabs/FieldConfigurationSidebar";
import { API_BASE_URL } from "../../../environment";
import { invalidateCachedRequest } from "../../../utils/requestCache";
import {
  showSuccessAlert,
  showErrorAlert,
  showInfoAlert,
} from "../../uiInterface/base-ui/alertservice";

const SourceSettingsLoader = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="sr-only">Loading...</span>
    </div>
    <span className="ms-2 text-muted">Loading Settings...</span>
  </div>
);

const route = all_routes;

type SourceItem = {
  id: string;
  name: string;
  order: number;
};

const SourceSettings = () => {
  const initialRef = useRef<SourceItem[]>([]);
  const { auth, selectedTenantId } = useContext(AuthContext);
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const moveStage = (sortedIdx: number, direction: number) => {
    const sorted = sources.slice().sort((a, b) => a.order - b.order);
    const targetIdx = sortedIdx + direction;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    [sorted[sortedIdx], sorted[targetIdx]] = [
      sorted[targetIdx],
      sorted[sortedIdx],
    ];
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setSources(reOrdered);
    setHasUnsavedChanges(true);
  };

  const updateStageName = (idx: number, value: string) => {
    const sorted = sources.slice().sort((a, b) => a.order - b.order);
    sorted[idx].name = value;
    setSources(sorted.map((s, i) => ({ ...s, order: i + 1 })));
    setHasUnsavedChanges(true);
  };

  const addStage = () => {
    const newStage: SourceItem = {
      id: `s${Date.now()}`,
      name: "",
      order: sources.length + 1,
    };
    setSources((prev) => [...prev, newStage]);
    setHasUnsavedChanges(true);
  };

  const removeStage = (sortedIdx: number) => {
    const sorted = sources.slice().sort((a, b) => a.order - b.order);
    sorted.splice(sortedIdx, 1);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));
    setSources(reOrdered);
    setHasUnsavedChanges(true);
  };

  const cancel = () => {
    setSources(initialRef.current);
    setHasUnsavedChanges(false);
  };

  useEffect(() => {
    if (!selectedTenantId) return;
    const loadSources = async () => {
      const authHeader = localStorage.getItem("authCred");
      if (!authHeader) return;

      setIsLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/lifecycle-source/?tenant_id=${selectedTenantId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error("Failed to load sources");
        }

        const data = await res.json();
        const results = data.results || data; // Handle paginated or direct array
        const formatted = results
          .filter((item: any) => item.is_active !== false)
          .sort((a: any, b: any) => a.source_order - b.source_order)
          .map((s: any) => ({
            id: String(s.lifecycle_source_id),
            name: s.lifecycle_source_display_name,
            order: s.source_order,
          }));

        setSources(formatted);
        initialRef.current = formatted;
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error("Error loading source settings:", err);
        showErrorAlert("Failed to load source settings.");
      } finally {
        setIsLoading(false);
      }
    };
    loadSources();
  }, [selectedTenantId]);

  const save = async () => {
    const authHeader = localStorage.getItem("authCred");
    if (!authHeader || !selectedTenantId || !auth?.user_id) {
      console.error("Missing required authentication or IDs.");
      return;
    }

    const nonEmpty = sources.filter((s) => s.name.trim() !== "");
    const sorted = nonEmpty.slice().sort((a, b) => a.order - b.order);
    const reOrdered = sorted.map((s, i) => ({ ...s, order: i + 1 }));

    const orders = reOrdered.map((s) => s.order);
    const unique = new Set(orders);
    if (unique.size !== orders.length) {
      await showErrorAlert("Each source must have a unique order number.");
      return;
    }

    const originalIds = new Set(initialRef.current.map((s) => s.id));
    const currentIds = new Set(reOrdered.map((s) => s.id));

    // Calculate changes
    const deletes = initialRef.current.filter(
      (s) => !currentIds.has(s.id) && !s.id.startsWith("s")
    );
    const inserts = reOrdered.filter((s) => s.id.startsWith("s"));
    const updates = reOrdered.filter(
      (s) => !s.id.startsWith("s") && originalIds.has(s.id)
    );

    let hasErrors = false;

    // Process Deletes (using PATCH to set is_active=false or DELETE)
    for (const del of deletes) {
      try {
        await fetch(`${API_BASE_URL}/lifecycle-source/${del.id}/`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        });
      } catch (error) {
        console.error(`Error deleting source ${del.id}:`, error);
        hasErrors = true;
      }
    }

    // Process Updates
    for (const update of updates) {
      const orig = initialRef.current.find((o) => o.id === update.id);
      if (orig && (orig.name !== update.name || orig.order !== update.order)) {
        try {
          await fetch(`${API_BASE_URL}/lifecycle-source/${update.id}/`, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
            body: JSON.stringify({
              lifecycle_source_display_name: update.name.trim(),
              source_order: update.order,
              updated_by: auth.user_id,
              tenant_id: selectedTenantId,
            }),
          });
        } catch (error) {
          console.error(`Error updating source ${update.id}:`, error);
          hasErrors = true;
        }
      }
    }

    // Process Inserts
    for (const insert of inserts) {
      try {
        await fetch(`${API_BASE_URL}/lifecycle-source/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
          body: JSON.stringify({
            lifecycle_source_display_name: insert.name.trim(),
            source_order: insert.order,
            is_active: true,
            tenant_id: selectedTenantId,
            created_by: auth.user_id,
            updated_by: auth.user_id,
          }),
        });
      } catch (error) {
        console.error(`Error inserting source ${insert.name}:`, error);
        hasErrors = true;
      }
    }

    invalidateCachedRequest("lifecycle-sources:");
    invalidateCachedRequest("filter-lifecycle-sources:");

    if (hasErrors) {
      await showErrorAlert("Some changes could not be saved.");
    } else {
      await showSuccessAlert("Source(s) updated successfully.");
    }

    // Reload sources to get fresh IDs
    setIsLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/lifecycle-source/?tenant_id=${selectedTenantId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        const results = data.results || data;
        const formatted = results
          .filter((item: any) => item.is_active !== false)
          .sort((a: any, b: any) => a.source_order - b.source_order)
          .map((s: any) => ({
            id: String(s.lifecycle_source_id),
            name: s.lifecycle_source_display_name,
            order: s.source_order,
          }));
        setSources(formatted);
        initialRef.current = formatted;
        setHasUnsavedChanges(false);
      }
    } catch (err) {
      console.error("Error reloading after save", err);
    } finally {
      setIsLoading(false);
    }
  };

  const sortedStages = sources.slice().sort((a, b) => a.order - b.order);

  const userRole = useTenantRole(selectedTenantId || 0);
  const canEdit = userRole?.role_key === "CLIENTADMIN";

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Settings</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body pb-0 pt-2">
                <SettingsTabsNavigation
                  activeTab="field-configuration"
                  variant="bottom"
                  includeIcons={true}
                  context="organization"
                />
              </div>
            </div>

            <div className="row">
              <FieldConfigurationSidebar />

              <div className="col-xl-9 col-lg-12">
                <div className="card">
                  <div className="card-body">
                    {isLoading ? (
                      <SourceSettingsLoader />
                    ) : (
                      <>
                        <div className="border-bottom mb-3 pb-3">
                          <h4 className="fw-semibold mb-1">Source Configuration</h4>
                          <p>Configure the source options below</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-medium">
                            Source Options
                          </label>
                        </div>
                        {sortedStages.map((stage, idx) => (
                          <div
                            key={stage.id}
                            className="d-flex align-items-center mb-2 p-2 border rounded w-20"
                          >
                            <input
                              type="text"
                              className="form-control me-3"
                              placeholder="Source name"
                              disabled={!canEdit}
                              value={stage.name}
                              onChange={(e) =>
                                updateStageName(idx, e.target.value)
                              }
                            />
                            {canEdit && (
                              <>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary me-1"
                                  onClick={(e) => {
                                    moveStage(idx, -1);
                                    e.currentTarget.blur();
                                  }}
                                  disabled={idx === 0}
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary me-3"
                                  onClick={(e) => {
                                    moveStage(idx, 1);
                                    e.currentTarget.blur();
                                  }}
                                  disabled={idx === sortedStages.length - 1}
                                >
                                  ▼
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={(e) => {
                                    removeStage(idx);
                                    e.currentTarget.blur();
                                  }}
                                >
                                  ✕
                                </button>
                              </>
                            )}
                          </div>
                        ))}

                        {canEdit && (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm mt-2 mb-4"
                              onClick={(e) => {
                                addStage();
                                e.currentTarget.blur();
                              }}
                            >
                              <i className="fa fa-plus" /> Add Source
                            </button>

                            <div>
                              <button
                                type="button"
                                className="btn btn-outline-secondary me-2"
                                onClick={(e) => {
                                  cancel();
                                  e.currentTarget.blur();
                                }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary me-2"
                                onClick={(e) => {
                                  save();
                                  e.currentTarget.blur();
                                }}
                              >
                                Save Changes
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SourceSettings;
